<template>
  <div class="pt-4 pb-6 md:py-6">
    <header-component :title="'¡'+ $t('dashboard.hello') + ' ' + authUserStore.user.firstname +'!'"></header-component>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <!-- Replace with your content -->
      <div class="py-4">
        <!-- Fila 1: Checklist de configuración, ancho completo -->
        <section class="pb-4">
          <div class="bg-white rounded-lg shadow-lg p-4 md:p-6">
            <template v-if="!allChecklistDone">
              <div class="flex items-center justify-between mb-1">
                <h2 class="text-lg font-semibold text-stone-700">{{ $t('dashboard.checklist_title') }}</h2>
                <span class="text-sm font-medium text-stone-400 flex-shrink-0 ml-2">{{ completedChecklistCount }}/{{ checklist.length }}</span>
              </div>
              <div class="w-full bg-stone-100 rounded-full h-2 mb-4">
                <div class="bg-green-500 h-2 rounded-full transition-all" :style="{ width: (completedChecklistCount / checklist.length * 100) + '%' }"></div>
              </div>

              <ol class="flex flex-col space-y-0.5 md:space-y-0 md:flex-row w-full">
                <li v-for="(item, index) in checklist" :key="item.key" class="relative md:flex-1">
                  <div v-if="index > 0" class="hidden md:block absolute top-4 right-1/2 w-full h-0.5"
                       :class="checklist[index - 1].done ? 'bg-green-500' : 'bg-stone-200'"></div>
                  <component :is="item.to ? 'router-link' : 'button'"
                             v-bind="item.to ? { to: item.to } : { type: 'button' }"
                             v-on="item.to ? {} : { click: productsStore.toggleModal }"
                             class="relative flex items-center md:flex-col md:items-center w-full py-2 md:py-0 rounded-md hover:bg-stone-50 md:hover:bg-transparent transition-all ease-in text-left group">
                    <span class="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                          :class="item.done ? 'bg-green-500' : 'border-2 border-stone-300 bg-white group-hover:border-stone-400'">
                      <svg v-if="item.done" class="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      <font-awesome-icon v-else :icon="item.icon" class="h-3.5 w-3.5 text-stone-300" />
                    </span>
                    <span class="ml-3 md:ml-0 md:mt-2 text-sm md:text-xs md:text-center" :class="item.done ? 'text-stone-700' : 'text-stone-700 font-medium'">{{ item.label }}</span>
                  </component>
                </li>
              </ol>
            </template>
            <div v-else class="flex flex-col items-center justify-center py-6">
              <span class="flex items-center justify-center w-14 h-14 rounded-full bg-green-500">
                <svg class="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </span>
              <span class="mt-3 text-base font-semibold text-stone-700">{{ $t('dashboard.checklist_complete') }}</span>
            </div>
          </div>
        </section>

        <!-- Fila 2: tarjetas de métricas, una sola fila en desktop -->
        <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-4">
          <button v-if="Number(productsReport.total) === 0" type="button" v-on:click="productsStore.toggleModal"
                  class="w-full h-32 flex flex-col items-center justify-center bg-white rounded-lg shadow-inner shadow-stone-200 border-2 border-bpblue hover:bg-bpblue/5 transition-colors relative text-center">
            <font-awesome-icon icon="chalkboard" class="absolute top-3 right-3 h-4 w-4 text-stone-300" />
            <span class="text-bpblue text-sm md:text-base lg:text-xl leading-6 font-semibold">
              {{ $t('dashboard.create_first_product') }}
            </span>
          </button>
          <div v-else>
            <default-card-component :quantity="productsReport.total" :loading="productsReport.loading" icon="chalkboard">
              {{ $t('general.products_temp') }}<!-- TEMPORAL: ver comentario junto a la clave en lang/es/general.js -->
            </default-card-component>
          </div>

          <div>
            <default-card-component :quantity="studentsReport.total" :loading="studentsReport.loading" icon="users">
              {{ $t('general.students') }}
            </default-card-component>
          </div>

          <default-card-component :quantity="annualReport.total + '€'" :loading="annualReport.loading" icon="chart-bar">
            {{ $t('general.annual_sales') + ' ' + annualReport.year }}
            <span v-if="annualReport.total === '0.00'" class="block text-xs text-stone-400 normal-case font-normal mt-1">{{ $t('dashboard.no_sales_yet') }}</span>
          </default-card-component>
          <default-card-component :quantity="monthlyReport.total + '€'" :loading="monthlyReport.loading" icon="calendar-day">
            {{ $t('general.sales') + ' ' + monthlyReport.month }}
            <span v-if="monthlyReport.total === '0.00'" class="block text-xs text-stone-400 normal-case font-normal mt-1">{{ $t('dashboard.no_sales_yet') }}</span>
          </default-card-component>
        </section>

        <!-- Fila 3: chatbot de la base de conocimiento + gráfica de ventas -->
        <section class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <kb-chatbot-widget></kb-chatbot-widget>
          <div class="min-w-0 bg-white rounded-lg shadow-lg p-2 md:p-4">
            <vue-apex-charts type="bar" :options="options"  :series="series"></vue-apex-charts>
          </div>
          <help-center-component class="md:col-span-2" :url="this.helpCenter.kn_dashboard_url"></help-center-component>
        </section>
      </div>
      <!-- /End replace -->
    </div>
  </div>

</template>

<script>
import HeaderComponent from "@/views/common/HeaderComponent";
import AnnualSalesService from "@/services/report/AnnualSalesService";
import MonthlySalesService from "@/services/report/MonthlySalesService";
import UsersReportService from "@/services/report/UsersReportService";
import moment from 'moment';
import DefaultCardComponent from "@/components/cards/DefaultCardComponent";
import ProducsReportService from "@/services/report/ProducsReportService";
import VueApexCharts from 'vue3-apexcharts';
import {useAuthUserStore} from "@/stores/auth";
import {useProductsStore} from "@/stores/products";
import {useAcademiesStore} from "@/stores/academies";
import HelpCenterComponent from "@/views/common/HelpCenterComponent";
import KbChatbotWidget from "@/components/panels/KbChatbotWidget";
import helpcenter from "@/services/helpcenter/links"

export default {
  name: "DashboardHelper",
  components: {DefaultCardComponent, HeaderComponent, VueApexCharts, HelpCenterComponent, KbChatbotWidget},
  data() {
    return {
      options: {
        chart: {
          id: 'monthly-sales',
          toolbar: {
            show: false
          }
        },
        xaxis: {
          categories: moment.monthsShort()
        }
      },
      series: [{
        name: 'Ventas mensuales (EUR)',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      }],
      annualReport: {
        loading: true,
        year: "",
        total: 0
      },
      monthlyReport: {
        loading: true,
        current_year: [],
        month: "",
        total: 0
      },
      studentsReport: {
        loading: true,
        total: 0
      },
      productsReport: {
        loading: true,
        total: 0
      },
      barChartData: {
        labels: moment.monthsShort(),
        datasets: [
          {
            label: 'Ventas mensuales',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            backgroundColor: '#3297cc'
          }
        ]
      },
      barChartOptions: {
        responsive: true,
        scales: {
          x: {
            ticks: {
              color: '#1F2937'
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: '#f3f8f8'
            }
          }
        }
      }
    }
  },
  setup () {

    const authUserStore = useAuthUserStore()
    const productsStore = useProductsStore()
    const academiesStore = useAcademiesStore()
    const helpCenter = helpcenter
    const academyStore = useAcademiesStore()

    return {
      authUserStore,
      productsStore,
      helpCenter,
      academyStore,
      academiesStore
    }
  },
  mounted() {
    this.academyStore.getAcademy().then(() => {
      this.getAnnualReport();
      this.getMonthlyReport();
      this.getUsersReport();
      this.getProductssReport();
    })
  },
  computed: {
    checklist() {
      return [
        {
          key: 'subdomain',
          done: !!this.academiesStore.academy.subdomain,
          to: '/academy/settings/domain',
          icon: 'globe',
          label: this.$t('dashboard.checklist_subdomain')
        },
        {
          key: 'logo',
          done: !!this.academiesStore.academy.branding.logo_url,
          to: '/academy/identity',
          icon: 'swatchbook',
          label: this.$t('dashboard.checklist_logo')
        },
        {
          key: 'brand',
          done: !!this.academiesStore.academy.title && !!this.academiesStore.academy.tagline,
          to: '/academy/identity',
          icon: 'swatchbook',
          label: this.$t('dashboard.checklist_brand')
        },
        {
          key: 'contact',
          done: !!this.academiesStore.academy.rrss.email && !!this.academiesStore.academy.rrss.phone,
          to: '/academy/contact',
          icon: 'id-card-alt',
          label: this.$t('dashboard.checklist_contact')
        },
        {
          key: 'product',
          done: Number(this.productsReport.total) !== 0,
          to: null,
          icon: 'chalkboard',
          label: this.$t('dashboard.checklist_product')
        },
      ]
    },
    completedChecklistCount() {
      return this.checklist.filter((item) => item.done).length
    },
    allChecklistDone() {
      return this.completedChecklistCount === this.checklist.length
    }
  },
  methods: {
    getAnnualReport() {
      this.annualReport.loading = true;
      var academy_uuid = this.academyStore.academy.uuid;
      AnnualSalesService.index(academy_uuid).then((response) => {
        console.log("Reporte anual de ventas", response)
        this.annualReport = response.data
        this.annualReport.total = parseFloat(this.annualReport.total).toFixed(2)
      }).catch((response) => {
        console.log("Error", response)
      })
          .finally(() => {
            this.annualReport.loading = false
          })
    },
    getMonthlyReport() {
      this.monthlyReport.loading = true;
      var academy_uuid = this.academyStore.academy.uuid;
      MonthlySalesService.index(academy_uuid).then((response) => {
        console.log("Reporte mensual de ventas", response)
        moment.locale("es");
        this.monthlyReport.month = moment(response.data.month, 'M').format('MMMM')
        this.monthlyReport.total = parseFloat(response.data.total).toFixed(2)
        this.monthlyReport.current_year = response.data.current_year

        this.monthlyReport.current_year.forEach((month) => {
            this.barChartData.datasets[0].data[parseInt(month.month) - 1] = parseFloat(month.sum).toFixed(2)
            this.series[0].data[parseInt(month.month) - 1] = parseFloat(month.sum).toFixed(2)
        })
      }).catch((response) => {
        console.log("Error", response)
      })
          .finally(() => {
            this.monthlyReport.loading = false
          })
    },
    getUsersReport() {
      this.studentsReport.loading = true;
      var academy_uuid = this.academyStore.academy.uuid;

      UsersReportService.index(academy_uuid).then((response) => {
        console.log("Reporte estudiantes", response)
        this.studentsReport.total = response.data.students.total
      })
          .catch((response) => {
            console.log("Error", response)
          })
          .finally(() => {
            this.studentsReport.loading = false
          })
    },
    getProductssReport() {
      this.productsReport.loading = true;
      var academy_uuid = this.academyStore.academy.uuid;

      ProducsReportService.index(academy_uuid).then((response) => {
        console.log("Reporte productos", response)
        this.productsReport.total = response.data.products.total
      })
          .catch((response) => {
            console.log("Error", response)
          })
          .finally(() => {
            this.productsReport.loading = false
          })
    }

  }
}
</script>

<style scoped>

</style>